import styled, { css } from 'styled-components';
import ComponentRenderer from '../../shared/ComponentRenderer';
import getMobileLayout from './shared/mobileLayouts';
import getDesktopLayout from './shared/desktopLayouts';
import { breakpoint } from 'variables';
import { Spacing16, Spacing32, Spacing64, Spacing8 } from 'autogen/design-tokens/tokens';
import type { TypeLayoutDetailed } from 'types/autogen/contentful';
import type { HTMLAttributes } from 'react';
import Layout from 'components/Layout';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { withErrorBoundary } from 'react-error-boundary';
import { filterPublishedFields } from 'components/pages/Contentful/shared/ContentfulHelpers';

// #region Styles
const resetRowColumnStyling = css`
    & > :nth-child(n) {
        grid-row: initial;
        grid-column: initial;
    }
`;

const getMobileGap = ($gap: TypeLayoutDetailed['fields']['gap']) => {
    return () => {
        switch ($gap) {
            case 'None':
                return css`gap: 0;`;
            case 'Small':
                return css`gap: ${Spacing8};`;
            case 'Large':
                return css`gap: ${Spacing32};`;
            case 'Medium':
            default:
                return css`
                    gap: ${Spacing16};
                `;
        }
    };
};

const getDesktopGap = ($gap: TypeLayoutDetailed['fields']['gap']) => {
    return () => {
        switch ($gap) {
            case 'None':
                return css`gap: 0;`;
            case 'Small':
                return css`gap: ${Spacing16};`;
            case 'Large':
                return css`gap: ${Spacing64};`;
            case 'Medium':
            default:
                return css`
                    gap: ${Spacing32};
                `;
        }
    };
};

const containerStyling = css<{
    $layoutOnMobile: TypeLayoutDetailed['fields']['layoutOnMobile'];
    $layoutOnDesktop: TypeLayoutDetailed['fields']['layoutOnDesktop'];
    $gap: TypeLayoutDetailed['fields']['gap'];
}>`
    display: grid;
    ${({ $gap }) => $gap && getMobileGap($gap)}
    ${({ $layoutOnMobile }) => getMobileLayout($layoutOnMobile)}

    ${breakpoint.up('1025px')} {
        ${({ $gap }) => $gap && getDesktopGap($gap)}

        ${({ $layoutOnDesktop }) =>
            $layoutOnDesktop &&
            css`
                ${resetRowColumnStyling}
                ${getDesktopLayout($layoutOnDesktop)}
            `}
    }

    // remove padding for nested layouts.
    & & {
        padding-left: 0;
        padding-right: 0;
    }

    &:empty {
        // to remove grid gap, if section has no children.
        display: none;
    }
`;

const Container = styled(Layout.Section)<{
    $layoutOnMobile: TypeLayoutDetailed['fields']['layoutOnMobile'];
    $layoutOnDesktop: TypeLayoutDetailed['fields']['layoutOnDesktop'];
    $gap: TypeLayoutDetailed['fields']['gap'];
}>`
    ${containerStyling}
`;

const ContainerWithBackground = styled(Layout.Section.WithFullWidthBackground)<{
    $layoutOnMobile: TypeLayoutDetailed['fields']['layoutOnMobile'];
    $layoutOnDesktop: TypeLayoutDetailed['fields']['layoutOnDesktop'];
    $gap: TypeLayoutDetailed['fields']['gap'];
}>`
    ${containerStyling}
`;

const Item = styled.div<{ $contentBackgroundColor: TypeLayoutDetailed['fields']['contentBackgroundColor'] }>`
    ${({ $contentBackgroundColor }) =>
        $contentBackgroundColor &&
        css`
            background-color: ${$contentBackgroundColor};
        `}
`;
// #endregion Styles

function Content({
    entries,
    contentBackgroundColor
}: { entries: TypeLayoutDetailed['fields']['entries']; contentBackgroundColor?: string }) {
    return entries?.map((data, index) => (
        <Item key={index} $contentBackgroundColor={contentBackgroundColor}>
            <ComponentRenderer {...data} />
        </Item>
    ));
}

export function LayoutRenderer(props: TypeLayoutDetailed & HTMLAttributes<HTMLDivElement>) {
    const {
        fields: { layoutOnMobile, layoutOnDesktop, entries, backgroundColor, gap, contentBackgroundColor, fullWidth },
        sys: { id },
        ...restProps
    } = props;
    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    const entriesToRender = filterPublishedFields(entries);

    if (backgroundColor) {
        return (
            <ContainerWithBackground
                backgroundColor={backgroundColor}
                contentRenderer={(children) => <>{children}</>}
                fullWidth={fullWidth}
                $layoutOnMobile={layoutOnMobile}
                $layoutOnDesktop={layoutOnDesktop}
                $gap={gap}
                {...inspectorProps({ fieldId: 'internalName' })}
                {...restProps}
            >
                <Content entries={entries} contentBackgroundColor={contentBackgroundColor} />
            </ContainerWithBackground>
        );
    }

    return (
        <Container
            contentRenderer={(children) => <>{children}</>}
            fullWidth={fullWidth}
            $layoutOnMobile={layoutOnMobile}
            $layoutOnDesktop={layoutOnDesktop}
            $gap={gap}
            {...inspectorProps({ fieldId: 'internalName' })}
            {...restProps}
        >
            <Content entries={entriesToRender} contentBackgroundColor={contentBackgroundColor} />
        </Container>
    );
}

export default withErrorBoundary(LayoutRenderer, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
